import { APIEndpoints } from '@app/enums/APIEndpoints';
import type { FileModel } from '@app/models/api/FileModel';
import type { SubscriptionCollectionModel } from '@app/models/api/SubscriptionCollectionModel';
import { translateSubscriptionCollectionResponseArray } from '@app/models/api/SubscriptionCollectionModel';
import type { SubscriptionModel } from '@app/models/api/SubscriptionModel';
import {
	translateSubscriptionResponse,
	translateSubscriptionResponseArray
} from '@app/models/api/SubscriptionModel';
import { downloadFile } from '../files';
import type { SignatureRequestModel } from '@app/models/api/SignatureRequestModel';
import { error, type HttpError } from '@sveltejs/kit';
import type { Fetch } from '@app/types/Global';
import type { subscriptionHistoryModel } from '@app/models/api/SubscriptionHistoryModel';
import { urls } from '@app/urls';
import { invalidateAll } from '$app/navigation';
import { snackbar } from '@wiseed/wikit';
import { t } from 'svelte-i18n';
import { get } from 'svelte/store';
import { redirectSignature } from '@app/utils/signature';
import type { SignatureAPIModel } from '@app/models/api/SignatureModel';
export const getPendingSubscription = async (
	sveltekitFetch: Fetch,
	status?: string[],
	paymentStatus?: string[]
): Promise<SubscriptionModel[]> => {
	const params = [];

	if (status && status.length > 0) {
		params.push(`status=${status.join('%2C')}`);
	}
	if (paymentStatus && paymentStatus.length > 0) {
		params.push(`paymentStatus=${paymentStatus.join('%2C')}`);
	}
	const queryParams = params.length > 0 ? `ongoing?${params.join('&')}` : `ongoing`;

	const res = await sveltekitFetch(APIEndpoints.INVESTMENTS(queryParams));

	const json = await res.json();
	if (res.ok) {
		if (res.status === 204 || !json?._embedded?.souscriptions) {
			return [];
		} else {
			return translateSubscriptionResponseArray(json?._embedded?.souscriptions);
		}
	} else {
		error(res.status, json);
	}
};

export const postSubscription = async (
	data: Record<string, any>,
	operationId: string
): Promise<SubscriptionModel> => {
	const res = await fetch(APIEndpoints.OPERATIONS(operationId.toString(), 'investments'), {
		method: 'POST',
		body: JSON.stringify(data)
	});
	const json = await res.json();
	if (res.ok) {
		return translateSubscriptionResponse(json);
	} else {
		const status = res.status;
		throw { status, ...json };
	}
};

export const postAifSharesSubscription = async (
	data: Record<string, any>,
	operationId: string
): Promise<SubscriptionModel> => {
	const res = await fetch(
		APIEndpoints.OPERATIONS_AIF_SHARES(operationId.toString(), 'investments'),
		{
			method: 'POST',
			body: JSON.stringify(data)
		}
	);
	const json = await res.json();
	if (res.ok) {
		return translateSubscriptionResponse(json);
	} else {
		throw json;
	}
};

export const postAifSharesSubscriptionInformations = async (
	data: Record<string, any>,
	operationId: string,
	subscriptionId: string
): Promise<SubscriptionModel> => {
	const fd = new FormData();
	fd.append('request', JSON.stringify(data.request));
	fd.append('rib', data.rib[0]);
	fd.append('dom', data.dom[0]);
	if (data.fileSup && data.fileSup[0]) {
		fd.append('fileSup', data.fileSup[0]);
	}
	const res = await fetch(
		APIEndpoints.OPERATIONS_AIF_SHARES(
			operationId.toString(),
			`investments/${subscriptionId}/informations`
		),
		{
			method: 'POST',
			body: fd
		}
	);
	const json = await res.json();
	if (res.ok) {
		return translateSubscriptionResponse(json);
	} else {
		throw json;
	}
};

export const getSignatureUrl = async (
	redirectBaseUrl: string,
	phone: string,
	phoneCodes: string,
	subscription: SubscriptionModel,
	operationId: string
): Promise<void> => {
	const payload: SignatureRequestModel = {
		urlRedirect: {
			successUrl: urls.SIGNATURE_REDIRECT_URL_SUCCESS(redirectBaseUrl),
			failUrl: urls.SIGNATURE_REDIRECT_URL_ERROR(redirectBaseUrl)
		},
		phone: {
			number: phone,
			country: phoneCodes
		}
	};

	if (!subscription?.id) return;

	const endpoint = subscription.isOPC
		? APIEndpoints.OPERATIONS_AIF_SHARES(operationId, `investments/${subscription.id}/signature`)
		: APIEndpoints.SUBSCRIPTION(subscription.id?.toString(), 'signature');

	await sendSignatureRequest(endpoint, payload);
};

const sendSignatureRequest = async (
	endpoint: string,
	data: SignatureRequestModel
): Promise<void> => {
	const res = await fetch(endpoint, {
		method: 'POST',
		body: JSON.stringify(data)
	});
	const json = await res.json();
	if (!res.ok) {
		error(res.status, json.message);
	}

	await redirectSignature(json as SignatureAPIModel);
};

export const handleSignatureError = (error: unknown) => {
	const httpError = error as HttpError;
	const $t = get(t);

	if (httpError.status === 503) {
		snackbar.danger($t('modalKYC.externalServiceUnavailable'));
	} else if (httpError.status === 401) {
		invalidateAll();
	} else {
		snackbar.danger(httpError?.body?.message ?? $t('subscription.signatureErrorMessage'));
	}
};

export const getSubscription = async (
	subscriptionId: string,
	sveltekitFetch?: Fetch
): Promise<SubscriptionModel> => {
	const url = APIEndpoints.ACCOUNT(`investments/${subscriptionId}`);
	const res = sveltekitFetch ? await sveltekitFetch(url) : await fetch(url);
	const json = await res.json();

	if (res.ok) {
		return translateSubscriptionResponse(json);
	} else {
		throw json;
	}
};

export const getSubscriptionHistory = async (
	operationTrancheId: number,
	operationId: number,
	sveltekitFetch?: Fetch
): Promise<subscriptionHistoryModel> => {
	const url = APIEndpoints.OPERATION_HISTORY(operationId, operationTrancheId);
	const res = sveltekitFetch ? await sveltekitFetch(url) : await fetch(url);
	const json = await res.json();
	if (res.ok) {
		return json;
	} else {
		throw json;
	}
};

export const payWithCreditCardAndRedirect = async (
	data: {
		urlRedirect: {
			successUrl: string;
			failUrl?: string;
		};
	},
	subscriptionId: string
): Promise<void> => {
	const res = await fetch(APIEndpoints.SUBSCRIPTION(subscriptionId.toString(), 'payment/card'), {
		method: 'POST',
		body: JSON.stringify(data)
	});
	const json = await res.json();

	if (!res.ok) {
		error(res.status, json);
	}
	if (json?.url) {
		return (window.location.href = json.url);
	}
	throw json;
};
export const payWithWallet = async (subscriptionId: string): Promise<any> => {
	const res = await fetch(APIEndpoints.SUBSCRIPTION(subscriptionId, 'payment/wallet'), {
		method: 'POST'
	});

	const json = await res.json();
	if (!res.ok) {
		error(res.status, json);
	}
	if (json?.code) {
		throw json;
	}
	return json;
};

export const payWithSimplifiedSDD = async (
	data: {
		bankId: number;
		urlRedirect: {
			successUrl: string;
			failUrl: string;
		};
	},
	subscriptionId: string
): Promise<string> => {
	const res = await fetch(
		APIEndpoints.SUBSCRIPTION(subscriptionId.toString(), 'payment/simplified-sdd'),
		{ method: 'POST', body: JSON.stringify(data) }
	);
	const json = await res.json();
	if (res.ok) {
		return json.url;
	} else {
		throw json;
	}
};

export const postIntention = async (operationId: string, data: { amount: number }) => {
	const res = await fetch(APIEndpoints.OPERATIONS(operationId, 'intention'), {
		method: 'POST',
		body: JSON.stringify(data)
	});
	const json = await res.json();
	if (res.ok) {
		return json;
	} else {
		throw json;
	}
};

export const postQueue = async (operationId: string, data?: { amount: number }) => {
	return await fetch(APIEndpoints.OPERATIONS(operationId, 'queue'), {
		method: 'POST',
		body: JSON.stringify(data)
	});
};

export const getSubscriptionsByOperation = async (
	operationId: string,
	sveltekitFetch?: Fetch
): Promise<SubscriptionCollectionModel[]> => {
	const url = APIEndpoints.OPERATIONS(operationId, 'investments');
	const res = sveltekitFetch ? await sveltekitFetch(url) : await fetch(url);
	const json = await res.json();
	if (res.ok) {
		return translateSubscriptionCollectionResponseArray(json?._embedded?.souscriptions);
	} else {
		throw json;
	}
};

export const downloadPackPEA = async (subscriptionId: string) => {
	const fm: FileModel = {
		creationDate: '',
		fileName: `PACK_PEA_${subscriptionId}.pdf`,
		fileType: 'pdf',
		id: 0,
		mimeType: 'application/pdf',
		title: `PACK_PEA_${subscriptionId}.pdf`,
		url: APIEndpoints.SUBSCRIPTION(subscriptionId.toString(), 'pea')
	};
	await downloadFile(fm);
};

export const downloadPaymentOrder = async (subscriptionId: string) => {
	const fm: FileModel = {
		creationDate: '',
		fileName: `IBAN_${subscriptionId}.pdf`,
		fileType: 'pdf',
		id: 0,
		mimeType: 'application/pdf',
		title: `IBAN_${subscriptionId}.pdf`,
		url: APIEndpoints.SUBSCRIPTION(subscriptionId, 'payment-order')
	};

	await downloadFile(fm);
};

export const getInvoice = async (subscriptionId: string): Promise<SubscriptionModel> => {
	const res = await fetch(APIEndpoints.ACCOUNT(`investments/${subscriptionId}`));
	const json = await res.json();
	if (res.ok) {
		return translateSubscriptionResponse(json);
	} else {
		throw json;
	}
};

export const getDocumentsSouscription = async (souscription: SubscriptionModel) => {
	const documents = [];
	const subscription = await getSubscription(souscription.id.toString());
	if (souscription && subscription.subscriptionForm) {
		documents.push({
			title: 'Bon de souscription signé',
			file: subscription.subscriptionForm
		});
	}

	return documents;
};

export const getConversions = async (sveltekitFetch: Fetch): Promise<SubscriptionModel[]> => {
	const res = await sveltekitFetch(APIEndpoints.INVESTMENTS('conversions'));
	const json = await res.json();
	if (res.ok) {
		if (res.status === 204 || !json?._embedded?.souscriptions) {
			return [];
		} else {
			return translateSubscriptionResponseArray(json?._embedded?.souscriptions);
		}
	} else {
		error(res.status, json);
	}
};

export const signConversion = async (
	data: SignatureRequestModel,
	subscriptionId: string
): Promise<void> => {
	const res = await fetch(APIEndpoints.SUBSCRIPTION(subscriptionId.toString(), 'conversion'), {
		method: 'POST',
		body: JSON.stringify(data)
	});
	const json = await res.json();
	if (!res.ok) {
		error(res.status, json.message);
	}

	await redirectSignature(json as SignatureAPIModel);
};

export const removeSubscriptionsDuplicates = (array: SubscriptionModel[]): SubscriptionModel[] => {
	const seenIds = new Set();
	const result: SubscriptionModel[] = [];

	for (let i = 0; i < array.length; i++) {
		if (!seenIds.has(array[i].id)) {
			result.push(array[i]);
			seenIds.add(array[i].id);
		}
	}

	return result;
};
